import React, { useEffect, useRef, useState, useMemo } from "react";
import { BoxGeometry, Mesh, MeshStandardMaterial, Vector3, Euler, Box3 } from "three";
import { preprocessFormulaAngles } from "../../helpers/evalCacheDimFormula";

// Valeurs par defaut (utilisees si aucune formule n'est fournie)
const DEFAULT_FORMULA_X = 'X * 0.1 - 18.6';
const DEFAULT_FORMULA_Y = 'Y * 0.1 - 8.5';
const DEFAULT_FORMULA_Z = 'Z * 0.1 - 6.0';

const cubeMaterialSettings = { color: 0xbbb0aa, emissive: 0x101010, emissiveIntensity: 1 };

/**
 * Valeur speciale pour la reference "repere principal"
 */
const MAIN_REFERENCE_ID = '__main_reference__';

/**
 * Evalue une formule JavaScript de maniere securisee.
 *
 * Variables disponibles :
 *   X, Y, Z       -> Dimensions du cache (profondeur, largeur, hauteur) en mm
 *   L, H, P1, P2  -> Dimensions de l'installation en mm
 *   DEC           -> Alias strict de DECi (retrocompat).
 *   DECi          -> Decalage derive du type d'installation (sol/sol-mur=-10,
 *                    mur/mur-haut=+10). Hardcode dans helpers/decHelpers.
 *   DECc          -> Decalage specifique au PRODUIT (Products.dec). null si
 *                    pas configure. Pour le fallback historique utiliser
 *                    explicitement `DECc ?? DECi`.
 *   INSTALL       -> Type d'installation (string : 'sol', 'sol-mur', 'mur',
 *                    'mur-haut'). Ex : `INSTALL === 'mur-haut' ? 40 : 0`.
 *
 * SYNC avec usipanel-backoffice/components/threeFiber/PlaceholderCube.jsx
 */
/**
 * Évalue une formule conditionnelle d'affichage (string JS).
 * Renvoie true si le placeholder doit être affiché, false sinon.
 *
 * Convention :
 * - formule absente / vide / null → true (toujours visible, défaut rétro-compat)
 * - formule renvoyant une valeur truthy → true (visible)
 * - formule renvoyant falsy ou erreur d'évaluation → false (masqué)
 *
 * Variables disponibles : X, Y, Z, L, H, P1, P2, DEC, DECi, DECc, INSTALL.
 *
 * SYNC avec usipanel-backoffice/components/threeFiber/PlaceholderCube.jsx
 */
// Proxy `math` (minuscule) aligné sur evalCacheDimFormula.js : expose toutes
// les méthodes de Math + un raccourci `math.radians(deg)`. Permet d'écrire
// `math.max(...)` / `math.cos(math.radians(2))` dans les formules de placeholder
// au même titre que dans les formules maîtresses du cache. `Math` (majuscule)
// reste évidemment utilisable (global JS implicite).
const PLACEHOLDER_MATH_PROXY = new Proxy(Math, {
  get(target, prop) {
    if (prop === 'radians') return (deg) => (deg * Math.PI) / 180;
    return Reflect.get(target, prop);
  },
});

export function evaluateDisplayFormula(formula, X, Y, Z, installDims = {}) {
  if (!formula || typeof formula !== 'string' || formula.trim() === '') {
    return true;
  }

  try {
    const L = installDims.L ?? 0;
    const H = installDims.H ?? 0;
    const P1 = installDims.P1 ?? 0;
    const P2 = installDims.P2 ?? 0;
    // DEC reste alias strict de DECi pour retrocompat. DECc peut etre null.
    const DECi = installDims.DECi ?? installDims.DEC ?? 0;
    const DECc = installDims.DECc ?? null;
    const DEC = installDims.DEC ?? DECi;
    const INSTALL = installDims.INSTALL ?? null;

    const safeFormula = preprocessFormulaAngles(formula);
    const fn = new Function(
      'X', 'Y', 'Z',
      'L', 'H', 'P1', 'P2', 'DEC', 'DECi', 'DECc', 'INSTALL',
      'Math', 'math',
      `return (${safeFormula})`
    );
    return Boolean(fn(X, Y, Z, L, H, P1, P2, DEC, DECi, DECc, INSTALL, Math, PLACEHOLDER_MATH_PROXY));
  } catch (err) {
    console.warn(`[PlaceholderCube] Erreur évaluation displayFormula "${formula}":`, err);
    return false;
  }
}

function evaluateFormula(formula, X, Y, Z, installDims = {}) {
  if (!formula || typeof formula !== 'string') {
    return 0;
  }

  try {
    const L = installDims.L ?? 0;
    const H = installDims.H ?? 0;
    const P1 = installDims.P1 ?? 0;
    const P2 = installDims.P2 ?? 0;
    const DECi = installDims.DECi ?? installDims.DEC ?? 0;
    const DECc = installDims.DECc ?? null;
    const DEC = installDims.DEC ?? DECi;
    const INSTALL = installDims.INSTALL ?? null;

    const safeFormula = preprocessFormulaAngles(formula);
    const fn = new Function(
      'X', 'Y', 'Z',
      'L', 'H', 'P1', 'P2', 'DEC', 'DECi', 'DECc', 'INSTALL',
      'Math', 'math',
      `return (${safeFormula})`
    );
    const result = fn(X, Y, Z, L, H, P1, P2, DEC, DECi, DECc, INSTALL, Math, PLACEHOLDER_MATH_PROXY);

    if (typeof result === 'number' && !isNaN(result) && isFinite(result)) {
      return result;
    }
    console.warn(`[PlaceholderCube] Formula "${formula}" returned invalid result:`, result);
    return 0;
  } catch (err) {
    console.error(`[PlaceholderCube] Error evaluating formula "${formula}":`, err);
    return 0;
  }
}

/**
 * Recupere la realSize stockee par FacesLoader sur le scene gltf enfant du
 * group mesh : { width, height, depth, bboxCenter }. Si indisponible, on
 * retombe sur une Box3.setFromObject classique (couteuse mais robuste).
 */
function getMeshBboxLocal(meshGroup) {
  const sceneChild = meshGroup?.children?.[0];
  const real = sceneChild?.userData?.realSize;
  if (real && real.width != null) {
    return {
      width: real.width,
      height: real.height,
      depth: real.depth,
      bboxCenter: real.bboxCenter || { x: 0, y: 0, z: 0 },
    };
  }
  // Fallback : box3 en world. NOTE : si le group est dans un parent scale != 1,
  // la valeur retournee est en world unit. C'est rarement exact pour notre cas.
  // On essaie quand meme pour ne pas planter.
  try {
    const box = new Box3().setFromObject(meshGroup);
    const size = new Vector3();
    const center = new Vector3();
    box.getSize(size);
    box.getCenter(center);
    return {
      width: size.x,
      height: size.y,
      depth: size.z,
      // bboxCenter ici est en WORLD, on le remet en local relatif a meshGroup.position
      bboxCenter: {
        x: center.x - meshGroup.position.x,
        y: center.y - meshGroup.position.y,
        z: center.z - meshGroup.position.z,
      },
    };
  } catch {
    return null;
  }
}

/**
 * Calcule la position d'un placeholder relative a une Part ou au repere principal.
 *
 * IMPORTANT : ce calcul est fait en ESPACE LOCAL du group parent qui contient
 * a la fois les meshes des parts et les placeholders. C'est independant de
 * tout scale parent applique au-dessus (e.g. <group scale=0.1> dans le renderer).
 *
 * @param {string|null} anchorPartId  ID de la Part de reference, ou MAIN_REFERENCE_ID
 * @param {string} anchorSide  'inside' | 'outside' | 'center'
 * @param {{x:number,y:number,z:number}} offset  Offset additionnel en convention PRODUIT
 *   (offset.x = profondeur, offset.y = largeur, offset.z = hauteur).
 *   Re-mappe en coordonnees Three.js (x=largeur, y=hauteur, z=profondeur) en sortie.
 * @param {Array<{mesh:THREE.Group, partInfo:{name:string}}>} meshes
 * @param {Array<Part>} parts
 * @param {{x:number,y:number,z:number}} placeholderDims Dimensions calculees du placeholder
 *   (convention PRODUIT : x=profondeur, y=largeur, z=hauteur, en mm).
 * @returns {Vector3} Position en mm dans l'espace local du group parent.
 */
function calculateRelativePosition(anchorPartId, anchorSide, offset, meshes, parts, placeholderDims) {
  const defaultPos = new Vector3(0, 0, 0);

  if (!anchorPartId || !meshes || meshes.length === 0) {
    return defaultPos;
  }

  // Repere principal = bas-arriere-centre de la bounding box globale de
  // l'assemblage, exprimee dans l'espace LOCAL du group parent (le meme
  // espace que celui des placeholders : mm, avant application du scale
  // FACE_SCALE du wrapper <group scale=0.1>).
  //
  // On NE peut PAS utiliser Box3.setFromObject (ou expandByObject) ici : il
  // appelle updateWorldMatrix() en remontant la chaine des matrices monde,
  // ce qui inclut le scale du parent. Selon que la matrixWorld du parent ait
  // ete mise a jour ou non par le cycle de rendu en cours, on obtiendrait
  // une bbox tantot en mm (correcte) tantot en world units scalees (10x
  // trop petite -> "teleport au centre" tres visible quand le client change
  // une dimension ou un type d'installation).
  //
  // Solution : on calcule la bbox a la main, en transformant manuellement
  // les 8 coins de chaque mesh local par la position+rotation du group.
  // On utilise userData.realSize stocke par FacesLoader (dimensions DXF
  // brutes) et la rotation du group (la face du dessous est tournee 90°
  // typiquement). C'est la combinaison rotation + dimensions reelles qui
  // donne la bonne bbox dans le repere parent — ni l'ancien algo (qui
  // ignorait la rotation) ni Box3.setFromObject (qui melange les echelles)
  // ne le faisait correctement dans le renderer.
  if (anchorPartId === MAIN_REFERENCE_ID) {
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let hasAny = false;

    const corner = new Vector3();
    for (const md of meshes) {
      if (!md?.mesh) continue;
      const local = getMeshBboxLocal(md.mesh);
      if (!local) continue;

      const meshGroup = md.mesh;
      const cx = local.bboxCenter?.x ?? 0;
      const cy = local.bboxCenter?.y ?? 0;
      const cz = local.bboxCenter?.z ?? 0;
      const hw = (local.width || 0) / 2;
      const hh = (local.height || 0) / 2;
      const hd = (local.depth || 0) / 2;

      const groupRot = new Euler(
        meshGroup.rotation?.x || 0,
        meshGroup.rotation?.y || 0,
        meshGroup.rotation?.z || 0,
        'XYZ'
      );
      const groupPos = meshGroup.position || { x: 0, y: 0, z: 0 };

      // 8 coins du local AABB du mesh, transformes par rotation+position du
      // group dans le repere parent. On accumule min/max sur ces 8 points
      // -> AABB englobante apres rotation, alignee axes parent.
      for (let dx = -1; dx <= 1; dx += 2) {
        for (let dy = -1; dy <= 1; dy += 2) {
          for (let dz = -1; dz <= 1; dz += 2) {
            corner.set(cx + dx * hw, cy + dy * hh, cz + dz * hd);
            corner.applyEuler(groupRot);
            corner.x += groupPos.x;
            corner.y += groupPos.y;
            corner.z += groupPos.z;
            if (corner.x < minX) minX = corner.x;
            if (corner.y < minY) minY = corner.y;
            if (corner.z < minZ) minZ = corner.z;
            if (corner.x > maxX) maxX = corner.x;
            if (corner.y > maxY) maxY = corner.y;
            if (corner.z > maxZ) maxZ = corner.z;
            hasAny = true;
          }
        }
      }
    }

    const position = new Vector3(0, 0, 0);
    if (hasAny) {
      position.set((minX + maxX) / 2, minY, minZ);
    }

    position.x += (offset?.y ?? 0);
    position.y += (offset?.z ?? 0);
    position.z += (offset?.x ?? 0);
    return position;
  }

  // Trouver la Part de reference
  const part = parts?.find(p => String(p.clientId || p.id) === String(anchorPartId));
  if (!part) {
    console.warn(`[PlaceholderCube] Part de reference "${anchorPartId}" non trouvee`);
    return defaultPos;
  }

  // Trouver le mesh correspondant
  const meshData = meshes.find(m => m.partInfo?.name === part.name);
  if (!meshData || !meshData.mesh) {
    console.warn(`[PlaceholderCube] Mesh pour la Part "${part.name}" non trouve`);
    return defaultPos;
  }

  const local = getMeshBboxLocal(meshData.mesh);
  if (!local) {
    return defaultPos;
  }

  // Appliquer la rotation Euler XYZ de la part au bboxCenter pour obtenir
  // le centre reel dans l'espace local du parent commun.
  const partRotation = new Euler(
    (part.rotationX ?? 0) * Math.PI / 180,
    (part.rotationY ?? 0) * Math.PI / 180,
    (part.rotationZ ?? 0) * Math.PI / 180,
    'XYZ'
  );
  const bcVec = new Vector3(local.bboxCenter.x, local.bboxCenter.y, local.bboxCenter.z);
  bcVec.applyEuler(partRotation);

  const meshPos = meshData.mesh.position;
  const center = new Vector3(meshPos.x + bcVec.x, meshPos.y + bcVec.y, meshPos.z + bcVec.z);

  // Profondeur effective sur l'axe Three.js Z. Pour rester aligne sur le BO
  // (qui utilise setFromObject), on prend la depth telle quelle. Les cas avec
  // rotation non nulle sur Y restent approximatifs mais conformes au BO.
  const partDepthZ = local.depth || 0;

  const position = center.clone();

  if (anchorSide === 'outside') {
    // SYNC BO : utilise placeholderDims.y (largeur en convention produit)
    // comme demi-epaisseur du placeholder sur l'axe Three.js Z. A garder
    // tel quel pour ne pas creer de divergence visuelle avec le BO.
    position.z += (partDepthZ / 2) + (placeholderDims.y / 2);
  } else if (anchorSide === 'inside') {
    position.z -= (partDepthZ / 2) - (placeholderDims.y / 2);
  }
  // 'center' = pas de decalage de face

  // Mapping convention PRODUIT -> Three.js
  //   offset.x (profondeur) -> position.z
  //   offset.y (largeur)    -> position.x
  //   offset.z (hauteur)    -> position.y
  position.x += (offset?.y ?? 0);
  position.y += (offset?.z ?? 0);
  position.z += (offset?.x ?? 0);

  return position;
}

const PlaceholderCube = ({
  size,
  hide3D,
  globalSceneSettings,
  placeholderFormulaX = DEFAULT_FORMULA_X,
  placeholderFormulaY = DEFAULT_FORMULA_Y,
  placeholderFormulaZ = DEFAULT_FORMULA_Z,
  placeholderColor,
  placeholderEmissive,
  placeholderEmissiveIntensity,
  anchorPartId,
  anchorSide = 'center',
  offsetX = 0,
  offsetY = 0,
  offsetZ = 0,
  meshes = [],
  parts = [],
  installDims = {},
  // Nom du placeholder (pour identifier les logs)
  name = 'Placeholder',
}) => {
  const [mesh, setMesh] = useState(null);
  const prevSize = useRef({});
  const prevFormulas = useRef({});
  const prevDims = useRef({});

  // size = { x: profondeur, y: largeur, z: hauteur } (backend convention, mm)
  // Les dimensions du placeholder sont calculees en mm, dans le meme espace
  // local que les meshes des parts (donc le <group scale=FACE_SCALE> qui les
  // wrappe transforme l'ensemble en world unit).
  const placeholderDimensions = useMemo(() => {
    const X = size.x;
    const Y = size.y;
    const Z = size.z;
    return {
      x: evaluateFormula(placeholderFormulaX || DEFAULT_FORMULA_X, X, Y, Z, installDims),
      y: evaluateFormula(placeholderFormulaY || DEFAULT_FORMULA_Y, X, Y, Z, installDims),
      z: evaluateFormula(placeholderFormulaZ || DEFAULT_FORMULA_Z, X, Y, Z, installDims),
    };
  }, [size.x, size.y, size.z, placeholderFormulaX, placeholderFormulaY, placeholderFormulaZ, installDims]);

  // Offsets en mm. Peuvent etre un nombre ou une chaine de formule (meme
  // evaluateur que pour les dimensions).
  const resolvedOffsets = useMemo(() => {
    const X = size.x;
    const Y = size.y;
    const Z = size.z;

    const resolve = (raw) => {
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'string' && raw.trim() !== '') {
        return evaluateFormula(raw, X, Y, Z, installDims);
      }
      return 0;
    };

    return {
      x: resolve(offsetX), // profondeur (X)
      y: resolve(offsetY), // largeur   (Y)
      z: resolve(offsetZ), // hauteur   (Z)
    };
  }, [offsetX, offsetY, offsetZ, size.x, size.y, size.z, installDims]);

  const placeholderPosition = useMemo(() => {
    // Aucune reference d'ancrage : offsets appliques par rapport a l'origine
    // du group parent (= origine de la scene 3D).
    if (!anchorPartId) {
      return new Vector3(
        resolvedOffsets.y || 0, // largeur    -> three.x
        resolvedOffsets.z || 0, // hauteur    -> three.y
        resolvedOffsets.x || 0, // profondeur -> three.z
      );
    }

    return calculateRelativePosition(
      anchorPartId,
      anchorSide,
      { x: resolvedOffsets.x, y: resolvedOffsets.y, z: resolvedOffsets.z },
      meshes,
      parts,
      placeholderDimensions
    );
  }, [anchorPartId, anchorSide, resolvedOffsets, meshes, parts, placeholderDimensions]);

  // Resolution des couleurs : props > globalSceneSettings > defauts
  const effectiveColor = placeholderColor || globalSceneSettings?.placeholderColor || 0xbbb0aa;
  const effectiveEmissive = placeholderEmissive || globalSceneSettings?.placeholderEmissive || 0x101010;
  const effectiveEmissiveIntensity = placeholderEmissiveIntensity ?? globalSceneSettings?.placeholderEmissiveIntensity ?? 1;

  useEffect(() => {
    if (hide3D) {
      return;
    }

    const sizeChanged = prevSize.current.x !== size.x || prevSize.current.y !== size.y || prevSize.current.z !== size.z;
    const formulasChanged = prevFormulas.current.x !== placeholderFormulaX ||
                           prevFormulas.current.y !== placeholderFormulaY ||
                           prevFormulas.current.z !== placeholderFormulaZ;
    const dimsChanged = prevDims.current.x !== placeholderDimensions.x ||
                        prevDims.current.y !== placeholderDimensions.y ||
                        prevDims.current.z !== placeholderDimensions.z;

    if (!mesh || sizeChanged || formulasChanged || dimsChanged) {
      // Three.js BoxGeometry: (width, height, depth)
      // Convention PRODUIT : X=profondeur, Y=largeur, Z=hauteur
      // -> width=Y(largeur), height=Z(hauteur), depth=X(profondeur)
      const geometry = new BoxGeometry(
        placeholderDimensions.y,
        placeholderDimensions.z,
        placeholderDimensions.x
      );
      const material = new MeshStandardMaterial({
        ...cubeMaterialSettings,
        color: effectiveColor,
        emissive: effectiveEmissive,
        emissiveIntensity: effectiveEmissiveIntensity,
      });
      const newMesh = mesh || new Mesh(geometry, material);
      newMesh.geometry = geometry;
      newMesh.material = material;
      newMesh.castShadow = false;
      newMesh.receiveShadow = true;
      newMesh.position.copy(placeholderPosition);
      newMesh.name = "placeholder";
      setMesh(newMesh);

      prevSize.current = { ...size };
      prevFormulas.current = {
        x: placeholderFormulaX,
        y: placeholderFormulaY,
        z: placeholderFormulaZ,
      };
      prevDims.current = { ...placeholderDimensions };
    } else {
      mesh.material.color.set(effectiveColor);
      mesh.material.emissive.set(effectiveEmissive);
      mesh.material.emissiveIntensity = effectiveEmissiveIntensity;
      mesh.position.copy(placeholderPosition);
    }
  }, [size.x, size.y, size.z, placeholderDimensions, placeholderPosition, placeholderFormulaX, placeholderFormulaY, placeholderFormulaZ, effectiveColor, effectiveEmissive, effectiveEmissiveIntensity, hide3D, mesh]);

  if (!mesh) return null;
  return <primitive object={mesh} />;
};

export default PlaceholderCube;
